<template>
  <div class="page-container insurances-page">
    <portal to="page-name">Planos de saúde</portal>
    <div class="card card-page">
      <div class="card-header">
        <h1 class="card-title">Planos de saúde</h1>
        <div class="columns">
          <div class="column">
            <div class="text-dark-gray mt-1">Gerencie os planos de saúde e particulares</div>
          </div>
          <div class="column text-right">
            <button class="btn btn-primary btn-icon btn-icon-left mr-2"
                    :disabled="deleting"
                    @click="open">
              <fa-icon :icon="['fal', 'plus']"/>Novo plano de saúde
            </button>
            <dx-dropdown
              class="btn btn-icon btn-action btn-gray-outline drop-menu input-group-addon"
              :class="{loading: printing}"
              :disabled="printing || deleting"
              :items="menuItems"
              @select="selectedMenu"
              label="name"
              direction="right"
            >
              <fa-icon :icon="['fal', 'bars']" />
            </dx-dropdown>
          </div>
        </div>
      </div>
      <div class="card-body">
        <div class="columns">
          <div class="column col-5 col-md-6 col-sm-12 form-group">
            <label class="form-label">Operadora do plano</label>
            <select class="form-select"
                    v-model="filter.insuranceId"
                    :disabled="deleting"
                    @change="find">
              <option value="">Todas as operadoras</option>
              <option v-for="(item, i) in insurances"
                      :value="item.id" :key="i">{{ item.name }}</option>
            </select>
          </div>
          <div class="column col-3 col-md-6 col-sm-12 form-group">
            <label class="form-label">Tipo</label>
            <select class="form-select"
                    v-model="filter.type"
                    :disabled="deleting"
                    @change="find">
              <option value="">Todos os tipos</option>
              <option v-for="(text, value) in insuranceTypes"
                      :value="value" :key="value">{{ text }}</option>
            </select>
          </div>
          <div class="column form-group">
            <label class="form-label">Nome do plano</label>
            <div class="input-group">
              <input type="text" class="form-input"
                     @keydown.enter="find"
                     v-model="filter.search"
                     :disabled="deleting"
                     placeholder="Pesquisar por nome do plano">
              <button class="btn btn-neutral btn-action input-group-btn btn-icon"
                      :disabled="deleting"
                      @click="find"
                      tabindex="-1">
                <fa-icon :icon="['fal', 'search']"/>
              </button>
            </div>
          </div>
        </div>
        <div class="loading loading-lg mt-2" v-if="loading" />
        <template v-else>
          <div class="empty mt-2" v-if="data.items.length === 0">
            <div class="empty-icon">
              <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
            </div>
            <p class="empty-title h5">Planos de saúde</p>
            <p class="empty-subtitle">
              Nenhum plano de saúde encontrado. Verifique os filtros para realizar novas buscas
            </p>
          </div>
          <table class="table table-striped table-hover" v-else>
            <thead>
            <tr>
              <th class="hide-sm" style="width: 40px">#</th>
              <th>Nome da Operadora</th>
              <th>Nome do plano</th>
              <th>Tipo</th>
              <th class="hide-sm text-center">Registro ANS</th>
              <th style="width: 80px"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, i) in data.items" :key="i">
              <td class="hide-sm">{{ i + 1 }}</td>
              <td>
                <span class="hover-link" v-if="deleting">{{ item.name }}</span>
                <router-link :to="editLink(item)" v-else>
                  {{ item.name }}
                </router-link>
              </td>
              <td>
                <span class="hover-link" v-if="deleting">
                  {{ item.plan.name }}
                </span>
                <router-link :to="editLink(item)" v-else>
                  {{ item.plan.name }}
                </router-link>
              </td>
              <td>{{ getTypeName(item.plan.type) }}</td>
              <td class="hide-sm text-center">
                <span>{{ item.plan.record || '-' }}</span>
              </td>
              <td class="text-right">
                <button class="btn btn-sm btn-action btn-icon btn-secondary mr-1"
                        :disabled="deleting"
                        @click="edit(item)">
                  <fa-icon :icon="['fal', 'pencil']" />
                </button>
                <button class="btn btn-sm btn-action btn-icon btn-error"
                        :class="{loading: deleting && idx === i}"
                        :disabled="deleting"
                        @click="remove(item)">
                  <fa-icon :icon="['fal', 'times']" />
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </template>
      </div>
      <div class="card-footer text-center" v-if="data.items.length > 0">
        <button
          v-if="data.hasMore && !loading"
          class="btn btn-primary btn-fixed-width"
          @click="loadMore"
          :disabled="loadingMore"
          :class="{ loading: loadingMore }"
        >Carregar mais</button>
      </div>
    </div>

    <insurance-modal
      v-if="insuranceModalShow"
      @save="saveInsurance"
      @remove="removeInsurance"
      @close="insuranceModalShow = false"
    />
  </div>
</template>

<script>
import moment from 'moment';
import {
  types as insuranceTypes,
  getName as getTypeName,
} from 'src/data/insurance-types';
import {
  UPDATE_INSURANCE,
  PRINT_INSURANCE_LIST,
  DELETE_INSURANCE,
} from '@/data/actions/modules/general';
import { mapState } from 'vuex';
import InsuranceModal from './modals/InsuranceModal.vue';

export default {
  components: { InsuranceModal },
  data() {
    return {
      idx: null,
      loading: false,
      loadingMore: false,
      deleting: false,
      printing: false,
      getTypeName,
      filter: {
        insuranceId: '',
        type: '',
        search: '',
      },
      insurances: [],
      insuranceTypes,
      menuItems: [
        { key: 'print', name: 'Imprimir' },
        { key: 'insurance', name: 'Gerenciar operadoras e agrupadores' },
      ],
      insuranceModalShow: false,
      data: {
        items: [],
        hasMore: false,
        limit: 0,
        offset: 0,
      },
    };
  },
  mounted() {
    this.loadInsurances();
    this.load();
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    canUpdateAccess() {
      return this.$can(UPDATE_INSURANCE);
    },
    canPrintAccess() {
      return this.$can(PRINT_INSURANCE_LIST);
    },
    canRemoveAccess() {
      return this.$can(DELETE_INSURANCE);
    },
  },
  methods: {
    async loadMore() {
      this.data.offset += this.data.limit;
      await this.load();
    },
    load() {
      const firstData = this.data.offset === 0;

      if (firstData) {
        this.loading = true;
      } else {
        this.loadingMore = true;
      }

      const params = {
        limit: this.data.limit,
        offset: this.data.offset,
        active: true,
      };

      if (this.filter.insuranceId) {
        params.insuranceId = this.filter.insuranceId;
      }

      if (this.filter.type) {
        params.type = this.filter.type;
      }

      if (this.filter.search.trim().length > 0) {
        params.search = this.filter.search.trim();
      }

      return this.$http.get('/insurance-plans', { params })
        .then(({ data }) => {
          this.data.hasMore = data.hasMore;
          this.data.items = firstData ? data.items : [...this.data.items, ...data.items];
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        })
        .finally(() => {
          this.loading = false;
          this.loadingMore = false;
        });
    },
    loadInsurances() {
      const params = {
        limit: 0,
        active: true,
      };
      return this.$http.get('/insurances', { params })
        .then(({ data }) => {
          this.insurances = data.items;
        })
        .catch(() => {});
    },
    find() {
      this.data.offset = 0;
      this.load();
    },
    edit(item) {
      if (this.deleting) {
        return;
      }
      this.$router.push(`/clinical/insurances/${item.id}/plans/${item.plan.id}/edit`);
    },
    editLink(item) {
      return `/clinical/insurances/${item.id}/plans/${item.plan.id}/edit`;
    },
    open() {
      this.$router.push('/clinical/insurance-plans/create');
    },
    remove(item) {
      if (!this.canRemoveAccess) {
        this.$toast.error('Acesso não autorizado!');
        return;
      }

      if (this.deleting) {
        return;
      }

      this.$dialog.show('', {
        size: 'md',
        html: `<div class="text-center">
                <p class="text-center h4">Atenção!</p>
                <p>Ao confirmar a remoção,
                 <strong class="text-primary">todos os valores</strong>
                 serão excluídos.</p>
                <p>Este registro também
                 <strong class="text-primary">será desvinculado</strong>
                 de todas as agendas e demais telas do sistema.</p>
                <div class="h6 text-error text-bold">
                 Essa ação é irreversível. Deseja continuar?
                </div>
                <br>
                <div>Responsável: <strong>${this.user.name}</strong></div>
                <div>
                  Data e hora: <strong>${moment().format('DD/MM/YYYY HH:mm')}</strong>
                </div>
              </div>`,
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              this.deleting = true;
              const idx = this.data.items.indexOf(item);
              this.idx = idx;
              close();
              this.$http
                .delete(`/insurances/${item.id}/plans/${item.plan.id}`)
                .then(() => {
                  this.data.items.splice(idx, 1);
                })
                .catch(() => {
                  this.$toast.error('Ocorreu um erro. Tente novamente!');
                })
                .finally(() => {
                  this.deleting = false;
                });
            },
          },
        ],
      });
    },
    selectedMenu(item) {
      if (item.key === 'insurance') {
        if (!this.canUpdateAccess) {
          this.$toast.error('Acesso não autorizado!');
          return;
        }
        this.insuranceModalShow = true;
      } if (item.key === 'print') {
        this.print();
      }
    },
    saveInsurance() {
      this.loadInsurances();
      this.insuranceModalShow = false;
    },
    removeInsurance(id) {
      this.data.items = this.data.items.filter(item => item.id !== id);
    },
    print() {
      if (!this.canPrintAccess) {
        this.$toast.error('Acesso não autorizado!');
        return null;
      }

      this.printing = true;

      const params = {
        active: true,
      };

      if (this.filter.insuranceId) {
        params.insuranceId = this.filter.insuranceId;
      }

      if (this.filter.type) {
        params.type = this.filter.type;
      }

      if (this.filter.search.trim().length > 0) {
        params.search = this.filter.search.trim();
      }

      return this.$file
        .print('/insurance-plans/print', params)
        .catch(() => {
          this.$toast.error('Ocorreu um erro ao imprimir! Tente novamente mais tarde.');
        })
        .finally(() => {
          this.printing = false;
        });
    },
  },
};
</script>

<style lang="scss">
  @import '@/assets/scss/_variables.scss';
  .insurances-page {
    .btn-fixed-width {
      font-size: $font-size-lg;
      height: 2.2rem;
      margin: $layout-spacing-lg 0;
      width: 11rem;
    }
    .drop-menu {
      line-height: 0;
      .menu {
        color: $dark-color;
        line-height: $layout-spacing-xl;
        text-align: left;
      }
      //.menu .menu-item > a, .menu .menu-item > .hover-link {
      //  color: $dark-color;
      //  text-align: left;
      //}
    }
  }
</style>
